import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ApiError,
  SessionStatus,
  api,
  loginRetryLine,
  rithmicLoginLine,
  useTrading,
  type ConnectionView,
  type LoginRetryLine,
} from "@helm/core";
import { connectionItem, type PlantInput } from "@helm/ui";
import { usePopover } from "../components/Popover";
import { Icon } from "../components/Icon";
import { DiagnosticsPanel } from "../shell/Diagnostics";
import { openRithmicDialog } from "./topbarDialogs";

const SESSION_WORDS: Partial<Record<SessionStatus, string>> = {
  [SessionStatus.DISCONNECTED]: "disconnected",
  [SessionStatus.CONNECTING]: "connecting",
  [SessionStatus.SYNCING]: "syncing",
  [SessionStatus.READY]: "ready",
  [SessionStatus.DEGRADED]: "degraded — reconnecting",
  [SessionStatus.KICKED]: "logged out by Rithmic",
};

/**
 * The desk's one connection state (owner 2026-10-04 UI clean-up): "Rithmic ● Connected /
 * Connecting / Disconnected / Rejected" in the status line. The gateway socket comes first (while
 * it is down nothing behind it is live), then the Rithmic login's order plant. Its menu has the
 * one-line detail the old connection banner showed ("Reconnecting — attempt 21"), whether what is
 * on screen is the last known state, "Log in to Rithmic…" / "Add Rithmic account…" (the Rithmic
 * login dialog) and Diagnostics (what the old bottom status bar showed).
 *
 * A refused login (owner decision 2026-10-04) adds one red line beside it: "Rithmic refused your
 * login (13 permission denied) — retrying in 30 s, try 2 of 5" with "Stop retrying", or "… gave
 * up after 5 tries" / "Rithmic rejected the password — not retried" with "Log in to Rithmic…".
 */
export function ConnectionItem({ connection }: { connection: ConnectionView }): React.JSX.Element {
  const login = useTrading((t) => t.login);
  const session = useTrading((t) => t.session);
  const line = rithmicLoginLine({ login, session });
  const plant: PlantInput =
    session === null
      ? null
      : {
          ready: session.status === SessionStatus.READY,
          words: SESSION_WORDS[session.status] ?? "not ready",
          detail: session.detail,
        };
  const item = connectionItem(connection, line, plant);
  // Only while the gateway is up: a dropped socket's last retry state is not current.
  const retry = connection.status === "connected" ? loginRetryLine(login, Date.now()) : null;
  const tone = retry ? "error" : item.tone;
  const pop = usePopover();
  const [diagnostics, setDiagnostics] = useState(false);
  const stop = useStopRetrying(retry?.loginId ?? null);
  return (
    <>
      <div className="popover-anchor statusline-conn-anchor">
        <button
          type="button"
          className="statusline-conn"
          data-state={tone}
          aria-haspopup="dialog"
          aria-label={`Rithmic connection: ${item.label}`}
          {...pop.triggerProps}
        >
          <span className="statusline-conn-name">Rithmic</span>
          <span className="statusline-conn-dot" aria-hidden="true" />
          <span className="statusline-conn-state">{item.label}</span>
        </button>
        {pop.open && (
          <div
            className="popover statusline-conn-menu"
            role="dialog"
            aria-label="Rithmic connection"
            {...pop.panelProps}
          >
            <div className="statusline-conn-head" data-state={tone}>
              <span className="statusline-conn-dot" aria-hidden="true" />
              <span className="statusline-conn-head-text">
                <span className="account-group-title">Rithmic connection</span>
                <span>
                  {line.system} · {item.label}
                </span>
              </span>
            </div>
            <p className="statusline-conn-detail" data-connection={connection.status}>
              {item.detail}
            </p>
            {retry && (
              <p className="statusline-conn-retry" data-testid="conn-menu-retry">
                {retry.text}
              </p>
            )}
            {item.stale && (
              <p className="statusline-conn-stale">
                Positions, orders and P&amp;L shown are the last known values, not live.
              </p>
            )}
            <div className="ra-menu-actions" role="group" aria-label="Rithmic logins">
              {retry?.action === "stop" && (
                <button
                  type="button"
                  className="account-item"
                  title="Stop the automatic retry of this login; it does not reconnect by itself after a restart either"
                  disabled={stop.busy}
                  onClick={() => {
                    pop.setOpen(false);
                    stop.run();
                  }}
                >
                  <Icon name="close" size={14} />
                  <span className="account-item-label">Stop retrying</span>
                </button>
              )}
              <button
                type="button"
                className="account-item"
                title="Connect or disconnect a stored Rithmic login; test it; Allow trading per login"
                onClick={() => {
                  pop.setOpen(false);
                  openRithmicDialog("login");
                }}
              >
                <Icon name="user" size={14} />
                <span className="account-item-label">Log in to Rithmic…</span>
              </button>
              <button
                type="button"
                className="account-item"
                title="Store a new Rithmic login (server, system, user name, password) and connect it"
                onClick={() => {
                  pop.setOpen(false);
                  openRithmicDialog("add");
                }}
              >
                <Icon name="plus" size={14} />
                <span className="account-item-label">Add Rithmic account…</span>
              </button>
              <button
                type="button"
                className={diagnostics ? "account-item active" : "account-item"}
                aria-expanded={diagnostics}
                onClick={() => {
                  setDiagnostics((d) => !d);
                }}
              >
                <Icon name="list" size={14} />
                <span className="account-item-label">Diagnostics</span>
                <Icon name="chevronDown" size={12} />
              </button>
            </div>
            {diagnostics && <DiagnosticsPanel />}
          </div>
        )}
      </div>
      {retry && <RetryLine retry={retry} login={login} stop={stop} />}
    </>
  );
}

type StopRetrying = { busy: boolean; error: string | null; run: () => void };

/** "Stop retrying" (helm-api, audited): the gateway's next `LoginStatus` shows the result. */
function useStopRetrying(loginId: string | null): StopRetrying {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
  }, [loginId]);
  return {
    busy,
    error,
    run: () => {
      if (loginId === null) return;
      setBusy(true);
      setError(null);
      api
        .stopRithmicRetry(loginId)
        .catch((e: unknown) => {
          setError(e instanceof ApiError || e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setBusy(false);
        });
    },
  };
}

/**
 * The red retry line: its text, then its action, as items of the status line itself. The text
 * takes the line's free room: the full sentence where it fits, a few words where it does not
 * (CSS container query, statusLine.css), ellipsized below that; the action is always whole. Its
 * countdown is the one timer of the refused-login state: while a try is scheduled it re-words
 * both texts in place once a second (no React render per tick).
 */
function RetryLine({
  retry,
  login,
  stop,
}: {
  retry: LoginRetryLine;
  login: Parameters<typeof loginRetryLine>[0];
  stop: StopRetrying;
}): React.JSX.Element {
  const text = useRef<HTMLSpanElement>(null);
  const full = useRef<HTMLSpanElement>(null);
  const short = useRef<HTMLSpanElement>(null);
  const word = (l: LoginRetryLine): void => {
    if (full.current) full.current.textContent = l.text;
    if (short.current) short.current.textContent = l.short;
    if (text.current) text.current.title = l.text;
  };
  useLayoutEffect(() => {
    word(retry);
  });
  const counting = retry.nextAtMs !== null;
  useEffect(() => {
    if (!counting) return undefined;
    const id = window.setInterval(() => {
      const now = loginRetryLine(login, Date.now());
      if (now) word(now);
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [counting, login]);
  const state = retry.action === "stop" ? "retrying" : "stopped";
  return (
    <>
      <span ref={text} className="statusline-retry-text" data-retry={state} title={retry.text}>
        <span ref={full} className="statusline-retry-full" data-testid="conn-retry-text" />
        <span
          ref={short}
          className="statusline-retry-short"
          data-testid="conn-retry-short"
          aria-hidden="true"
        />
      </span>
      {retry.action === "stop" ? (
        <button
          type="button"
          className="statusline-retry-action"
          data-retry={state}
          data-testid="conn-retry-action"
          disabled={stop.busy}
          title="Stop the automatic retry of this login; it does not reconnect by itself after a restart either"
          onClick={stop.run}
        >
          {stop.busy ? "Stopping…" : "Stop retrying"}
        </button>
      ) : (
        <button
          type="button"
          className="statusline-retry-action"
          data-retry={state}
          data-testid="conn-retry-action"
          onClick={() => {
            openRithmicDialog("login");
          }}
        >
          Log in to Rithmic…
        </button>
      )}
      {stop.error && (
        <span className="statusline-retry-error" role="alert">
          Could not stop retrying: {stop.error}
        </span>
      )}
    </>
  );
}
